import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams, useSearchParams } from "react-router-dom";
import { getStaffSlug } from "@/lib/cafe/staffSession";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/sonner";
import Index from "./pages/Index";
import Health from "./pages/Health";

// Plan 03-10: enrolment surfaces are lazy-loaded so the marketing landing
// (Index) doesn't pull in libphonenumber-js + react-hook-form + zod +
// @marsidev/react-turnstile on first paint. Phase 3 surfaces are mounted
// only when the customer scans a QR (`/?venue=<slug>` triggers the dynamic
// chunk via the same path matcher — ?venue handling lives inside Enrol).
// Plan 03-06: Welcome page lazy-loaded for the same reason (framer-motion +
// shadcn primitives + sonner toasts + the 4 perk subviews live inside).
const Enrol = lazy(() => import("./pages/Enrol"));
const OtpEntry = lazy(() => import("./pages/OtpEntry"));
const Welcome = lazy(() => import("./pages/Welcome"));
// Phase 5 plan 05-06 — /me customer page (multi-venue stamp grid + tier
// indicator + RewardListener). Lazy-loaded so the marketing landing keeps
// its tight first-paint budget (UI-SPEC §8 budget ≤35 KB gzip).
const Me = lazy(() => import("./pages/Me"));
// Phase 6 plan 06-04 — /reward customer reward card (auth-gated QR + 4-digit
// code + 60s countdown + refresh; D-02 poll → "Utilisée ✓"; D-03 Page-
// Visibility auto-rotate). Its own lazy chunk — qrcode.react rides this chunk;
// the scanner WASM does NOT (that's Plan 05's separate /cafe chunk).
const Reward = lazy(() => import("./pages/Reward"));
// G2 fix (2026-07-16 gap audit) — standalone legal pages. The enrolment consent
// "read" links open these in a new tab (#consent-…-fulltext anchors resolve
// here); the marketing footer and /me DataRightsCard link them permanently.
const PrivacyPolicy = lazy(() => import("./pages/legal/PrivacyPolicy"));
const TermsOfUse = lazy(() => import("./pages/legal/TermsOfUse"));
// Pre-printed plaque scan target (spec 2026-07-17): /q/<code> resolves the
// QR pool and forwards to /?venue=<slug>, or shows the branded holding page.
const QrLanding = lazy(() => import("./pages/QrLanding"));

// Staff-dashboard access (2026-07-16): the standalone /cafe/* staff terminal
// is RETIRED. Its scan flow lives at /merchant/<slug>/scan (MerchantScan) and
// staff PIN login is the Équipe tab on /merchant/<slug>/login. The old URLs
// (printed cards / bookmarks) redirect below — see CafeScanRedirect /
// CafeLoginRedirect near the routes.

// Plan 10-09: merchant self-service dashboard — slug-prefixed routing per
// CONTEXT D-01. MerchantShell is the parent route layout (header + Tabs nav
// + auth guard + dirty-form provider + <Outlet />); 4 lazy children. The
// merchant chunks DO NOT load on the customer enrolment path (route-level
// code splitting) — only when an operator hits /merchant/<slug>/...
const MerchantShell = lazy(() => import("./components/merchant/MerchantShell"));
const MerchantAuth = lazy(() => import("./pages/merchant/Auth"));
const MerchantOverview = lazy(() => import("./pages/merchant/Overview"));
const MerchantAudience = lazy(() => import("./pages/merchant/Audience"));
const MerchantLanding = lazy(() => import("./pages/merchant/Landing"));
const MerchantDesignStudio = lazy(() => import("./pages/merchant/DesignStudio"));
const MerchantPerk = lazy(() => import("./pages/merchant/Perk"));
const MerchantPerks = lazy(() => import("./pages/merchant/Perks"));
const MerchantMenu = lazy(() => import("./pages/merchant/Menu"));
const MerchantCampaigns = lazy(() => import("./pages/merchant/Campaigns"));
const MerchantReceiptReviewQueue = lazy(() => import("./pages/merchant/ReceiptReviewQueue"));
const MerchantRewards = lazy(() => import("./pages/merchant/Rewards"));
const MerchantScan = lazy(() => import("./pages/merchant/Scan"));
const MerchantEquipe = lazy(() => import("./pages/merchant/Equipe"));
// Public anonymous menu page (Q2: anonymous gets read-only menu, no discounts).
const MenuPublic = lazy(() => import("./pages/Menu"));
// Public presentation page — themed fidelity-program showcase (spec
// 2026-07-13 §7). Own lazy chunk, separate from the enrol/menu chunks.
const PresentationPublic = lazy(() => import("./pages/PresentationPublic"));
// Email + password sign-in (typical login workflow). Account creation lives
// at /signup. The slug-targeted /merchant/<slug>/login (MerchantAuth) is the
// same password form, slug-aware, for deep links. Magic-link sign-in was
// retired 2026-07-12 (undeliverable in production).
const MerchantLogin = lazy(() => import("./pages/merchant/Login"));
const MerchantSignup = lazy(() => import("./pages/merchant/Signup"));
// Shared password-recovery surfaces (operator + merchant). Top-level routes so
// they render outside the auth-guarded shells. /reset-password is the branded
// recovery email's redirect target — keep it in the Supabase redirect allowlist.
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));

// Plan 09-06: operator admin console — network-wide (NOT slug-prefixed) per
// CONTEXT D-01. AdminShell is the parent route layout (sticky header + 7-tab
// nav + auth guard + dirty-form provider + <Outlet />). This plan wires the
// `login` + `operators` children + the index Navigate; Plans 09-07/08/09 add
// their own section children (venues / tiers / deletion-queue / pixel-log /
// phone-migration), each editing App.tsx for ITS page only. The index target
// `/admin/venues` is added by Plan 09-07 — until then the index lands on a
// not-yet-mounted child (transient interim target, documented in 09-06-SUMMARY).
const AdminShell = lazy(() => import("./components/admin/AdminShell"));
const AdminAuth = lazy(() => import("./pages/admin/AdminAuth"));
const AdminOperators = lazy(() => import("./pages/admin/Operators"));
const AdminVenues = lazy(() => import("./pages/admin/Venues"));
// QR pool (spec 2026-07-17): generate/link/unlink pre-printable codes +
// 18×18 cm plaque printing.
const AdminQrCodes = lazy(() => import("./pages/admin/QrCodes"));
const AdminTiers = lazy(() => import("./pages/admin/Tiers"));
const AdminDeletionQueue = lazy(() => import("./pages/admin/DeletionQueue"));
const AdminCampaigns = lazy(() => import("./pages/admin/Campaigns"));
const AdminPixelLog = lazy(() => import("./pages/admin/PixelLog"));
const AdminPhoneMigration = lazy(() => import("./pages/admin/PhoneMigration"));
// OTP anti-abuse review queue (Task 9): operator Unblock / Approve change /
// Dismiss on flags raised by admin-review-flag (ladder exhaustion, IP flood,
// repeat phone-change).
const AdminFlaggedNumbers = lazy(() => import("./pages/admin/FlaggedNumbers"));
// Super-admin panel (2026-07-12): network audience visualization + network
// campaigns sent as Medina Fidélité.
const AdminAudiences = lazy(() => import("./pages/admin/Audiences"));
const AdminNetworkCampaigns = lazy(
  () => import("./pages/admin/NetworkCampaigns"),
);

// Design exploration — 10 QR-landing mockups (evaluation only, no real data).
const MockupsIndex = lazy(() => import("./pages/mockups/Mockups"));
const Mockup01 = lazy(() => import("./pages/mockups/Mockup01"));
const Mockup02 = lazy(() => import("./pages/mockups/Mockup02"));
const Mockup03 = lazy(() => import("./pages/mockups/Mockup03"));
const Mockup04 = lazy(() => import("./pages/mockups/Mockup04"));
const Mockup05 = lazy(() => import("./pages/mockups/Mockup05"));
const Mockup06 = lazy(() => import("./pages/mockups/Mockup06"));
const Mockup07 = lazy(() => import("./pages/mockups/Mockup07"));
const Mockup08 = lazy(() => import("./pages/mockups/Mockup08"));
const Mockup09 = lazy(() => import("./pages/mockups/Mockup09"));
const Mockup10 = lazy(() => import("./pages/mockups/Mockup10"));
const Mockup11 = lazy(() => import("./pages/mockups/Mockup11"));
const Mockup12 = lazy(() => import("./pages/mockups/Mockup12"));
const Mockup13 = lazy(() => import("./pages/mockups/Mockup13"));
const Mockup14 = lazy(() => import("./pages/mockups/Mockup14"));
const Mockup15 = lazy(() => import("./pages/mockups/Mockup15"));
const Mockup16 = lazy(() => import("./pages/mockups/Mockup16"));
const Mockup17 = lazy(() => import("./pages/mockups/Mockup17"));
const Mockup18 = lazy(() => import("./pages/mockups/Mockup18"));
const Mockup19 = lazy(() => import("./pages/mockups/Mockup19"));
const Mockup20 = lazy(() => import("./pages/mockups/Mockup20"));
const Mockup21 = lazy(() => import("./pages/mockups/Mockup21"));
const Mockup22 = lazy(() => import("./pages/mockups/Mockup22"));
const Mockup23 = lazy(() => import("./pages/mockups/Mockup23"));
const Mockup24 = lazy(() => import("./pages/mockups/Mockup24"));
const Mockup25 = lazy(() => import("./pages/mockups/Mockup25"));
const Mockup26 = lazy(() => import("./pages/mockups/Mockup26"));
const Mockup27 = lazy(() => import("./pages/mockups/Mockup27"));
const Mockup28 = lazy(() => import("./pages/mockups/Mockup28"));
const Mockup29 = lazy(() => import("./pages/mockups/Mockup29"));
const Mockup30 = lazy(() => import("./pages/mockups/Mockup30"));
const Mockup31 = lazy(() => import("./pages/mockups/Mockup31"));
const Mockup32 = lazy(() => import("./pages/mockups/Mockup32"));
const Mockup33 = lazy(() => import("./pages/mockups/Mockup33"));
const Mockup34 = lazy(() => import("./pages/mockups/Mockup34"));
const Mockup35 = lazy(() => import("./pages/mockups/Mockup35"));
const Mockup36 = lazy(() => import("./pages/mockups/Mockup36"));
const Mockup37 = lazy(() => import("./pages/mockups/Mockup37"));
const Mockup38 = lazy(() => import("./pages/mockups/Mockup38"));
const Mockup39 = lazy(() => import("./pages/mockups/Mockup39"));
const Mockup40 = lazy(() => import("./pages/mockups/Mockup40"));

function HealthThrowPage(): never {
  throw new Error("Sentry pipeline test — /_health/throw intentional error");
}

// Single QueryClient instance shared across the app. Default options keep
// retries minimal (the venue lookup + edge-fn calls have their own retry
// semantics; we don't want TanStack Query to layer additional retries on
// top — failures should surface to the UI promptly).
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const SuspenseFallback = () => (
  <div className="min-h-screen bg-background" />
);

// Legacy /cafe/* forwards (staff-dashboard access, 2026-07-16). A live staff
// session remembers its slug in sessionStorage; without one there is no venue
// to route to, so fall back to the root.
function CafeScanRedirect() {
  const slug = getStaffSlug();
  return <Navigate to={slug ? `/merchant/${slug}/scan` : "/"} replace />;
}
// /cafe/<slug> carried the venue in the URL → dashboard login, Équipe tab.
function CafeLoginRedirect() {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={`/merchant/${slug}/login?staff=1`} replace />;
}

// Conditional root: marketing landing for visitors hitting medinafidelite.ma,
// QR enrolment gateway when ?venue=<slug> is present (printed QR codes point
// at /?venue=<slug>; preserving that contract while restoring the marketing
// page at the bare root).
function Root() {
  const [params] = useSearchParams();
  if (params.has("venue")) {
    return (
      <Suspense fallback={<SuspenseFallback />}>
        <Enrol />
      </Suspense>
    );
  }
  return <Index />;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          {/*
           * Plan 03-10: the marketing landing (Index) and the QR gateway
           * (Enrol) both live at "/". Enrol takes precedence when ?venue is
           * present in the URL; otherwise Index renders the marketing page.
           * For the v1 wave we ship a router-level disambiguation by
           * pointing "/" at Enrol — Enrol's missingVenueParam branch falls
           * back to a guidance message for users who deep-link without a
           * venue slug (Surface 9 missingVenueParam). The existing Index
           * marketing page moves to "/about" so it stays accessible until
           * the dedicated marketing domain split in Phase 8.
           */}
          <Route path="/" element={<Root />} />
          <Route path="/about" element={<Index />} />
          <Route
            path="/enrol/otp"
            element={
              <Suspense fallback={<SuspenseFallback />}>
                <OtpEntry />
              </Suspense>
            }
          />
          {/*
           * Plan 03-06: Surface 4 — /welcome page renders the venue's
           * welcome perk (4 perk-shape sub-views via D-06 discriminated
           * union) + stamp preview + Bronze tier pill. Mount priority chain:
           * React Router nav state (D-04) → sessionStorage `mf_perk_<id>`
           * (5-min TTL) → grant-welcome-perk fetch (D-03 hybrid contract).
           */}
          <Route
            path="/welcome"
            element={
              <Suspense fallback={<SuspenseFallback />}>
                <Welcome />
              </Suspense>
            }
          />
          {/*
           * Phase 5 plan 05-06: /me customer page — multi-venue stamp grid +
           * network tier indicator + RewardListener (D-14). Auth-gated via
           * useSessionCheck (D-07/D-08 redirect logic).
           */}
          <Route
            path="/me"
            element={
              <Suspense fallback={<SuspenseFallback />}>
                <Me />
              </Suspense>
            }
          />
          {/*
           * Phase 6 plan 06-04: /reward?id=<uuid> — customer reward card.
           * Auth-gated via useSessionCheck + get-reward ownership scoping
           * before trusting the `?id` (RED-01 / C-03). Lazy chunk alongside
           * /me; the RewardListener seam (RewardEarnedDialog CTA) navigates
           * here.
           */}
          <Route
            path="/reward"
            element={
              <Suspense fallback={<SuspenseFallback />}>
                <Reward />
              </Suspense>
            }
          />
          {/*
           * G2 fix (2026-07-16 gap audit): permanent legal routes. French
           * slugs match the product's user-facing language (domain rule:
           * medinafidelite.ma). Content renders in fr/ar via i18n.
           */}
          <Route
            path="/confidentialite"
            element={
              <Suspense fallback={<SuspenseFallback />}>
                <PrivacyPolicy />
              </Suspense>
            }
          />
          <Route
            path="/conditions"
            element={
              <Suspense fallback={<SuspenseFallback />}>
                <TermsOfUse />
              </Suspense>
            }
          />
          {/*
           * Pre-printed plaque scan target (spec 2026-07-17): one anon
           * lookup, then in-app forward to the enrolment gateway — or the
           * venue-neutral bilingual holding page (unlinked/unknown/inactive).
           */}
          <Route
            path="/q/:code"
            element={
              <Suspense fallback={<SuspenseFallback />}>
                <QrLanding />
              </Suspense>
            }
          />
          {/*
           * Staff-dashboard access (2026-07-16): the /cafe/* terminal is
           * retired; these routes only forward legacy URLs (printed cards,
           * bookmarks) into the dashboard. Literals stay declared before the
           * /cafe/:slug param route. There is still NO bare /cafe route.
           */}
          <Route path="/cafe/scan" element={<CafeScanRedirect />} />
          <Route path="/cafe/today" element={<CafeScanRedirect />} />
          <Route path="/cafe/:slug" element={<CafeLoginRedirect />} />
          <Route path="/health" element={<Health />} />
          {/* Public anonymous menu page — Q2 product decision (May 2026):
              anonymous visitors see prices without the discount overlay. The
              enrolled-customer view lives inside /welcome's Menu tab. */}
          <Route
            path="/menu/:slug"
            element={
              <Suspense fallback={<SuspenseFallback />}>
                <MenuPublic />
              </Suspense>
            }
          />
          {/* Public presentation page — themed fidelity-program showcase
              (spec 2026-07-13 §7). Redirects to the enrol landing when
              page_presentation_enabled=false. */}
          <Route
            path="/fidelite/:slug"
            element={
              <Suspense fallback={<SuspenseFallback />}>
                <PresentationPublic />
              </Suspense>
            }
          />
          {/* Design exploration — see /mockups for the index. */}
          <Route
            path="/mockups"
            element={
              <Suspense fallback={<SuspenseFallback />}>
                <MockupsIndex />
              </Suspense>
            }
          />
          <Route
            path="/mockups/01"
            element={
              <Suspense fallback={<SuspenseFallback />}>
                <Mockup01 />
              </Suspense>
            }
          />
          <Route path="/mockups/02" element={<Suspense fallback={<SuspenseFallback />}><Mockup02 /></Suspense>} />
          <Route path="/mockups/03" element={<Suspense fallback={<SuspenseFallback />}><Mockup03 /></Suspense>} />
          <Route path="/mockups/04" element={<Suspense fallback={<SuspenseFallback />}><Mockup04 /></Suspense>} />
          <Route path="/mockups/05" element={<Suspense fallback={<SuspenseFallback />}><Mockup05 /></Suspense>} />
          <Route path="/mockups/06" element={<Suspense fallback={<SuspenseFallback />}><Mockup06 /></Suspense>} />
          <Route path="/mockups/07" element={<Suspense fallback={<SuspenseFallback />}><Mockup07 /></Suspense>} />
          <Route path="/mockups/08" element={<Suspense fallback={<SuspenseFallback />}><Mockup08 /></Suspense>} />
          <Route path="/mockups/09" element={<Suspense fallback={<SuspenseFallback />}><Mockup09 /></Suspense>} />
          <Route path="/mockups/10" element={<Suspense fallback={<SuspenseFallback />}><Mockup10 /></Suspense>} />
          <Route path="/mockups/11" element={<Suspense fallback={<SuspenseFallback />}><Mockup11 /></Suspense>} />
          <Route path="/mockups/12" element={<Suspense fallback={<SuspenseFallback />}><Mockup12 /></Suspense>} />
          <Route path="/mockups/13" element={<Suspense fallback={<SuspenseFallback />}><Mockup13 /></Suspense>} />
          <Route path="/mockups/14" element={<Suspense fallback={<SuspenseFallback />}><Mockup14 /></Suspense>} />
          <Route path="/mockups/15" element={<Suspense fallback={<SuspenseFallback />}><Mockup15 /></Suspense>} />
          <Route path="/mockups/16" element={<Suspense fallback={<SuspenseFallback />}><Mockup16 /></Suspense>} />
          <Route path="/mockups/17" element={<Suspense fallback={<SuspenseFallback />}><Mockup17 /></Suspense>} />
          <Route path="/mockups/18" element={<Suspense fallback={<SuspenseFallback />}><Mockup18 /></Suspense>} />
          <Route path="/mockups/19" element={<Suspense fallback={<SuspenseFallback />}><Mockup19 /></Suspense>} />
          <Route path="/mockups/20" element={<Suspense fallback={<SuspenseFallback />}><Mockup20 /></Suspense>} />
          <Route path="/mockups/21" element={<Suspense fallback={<SuspenseFallback />}><Mockup21 /></Suspense>} />
          <Route path="/mockups/22" element={<Suspense fallback={<SuspenseFallback />}><Mockup22 /></Suspense>} />
          <Route path="/mockups/23" element={<Suspense fallback={<SuspenseFallback />}><Mockup23 /></Suspense>} />
          <Route path="/mockups/24" element={<Suspense fallback={<SuspenseFallback />}><Mockup24 /></Suspense>} />
          <Route path="/mockups/25" element={<Suspense fallback={<SuspenseFallback />}><Mockup25 /></Suspense>} />
          <Route path="/mockups/26" element={<Suspense fallback={<SuspenseFallback />}><Mockup26 /></Suspense>} />
          <Route path="/mockups/27" element={<Suspense fallback={<SuspenseFallback />}><Mockup27 /></Suspense>} />
          <Route path="/mockups/28" element={<Suspense fallback={<SuspenseFallback />}><Mockup28 /></Suspense>} />
          <Route path="/mockups/29" element={<Suspense fallback={<SuspenseFallback />}><Mockup29 /></Suspense>} />
          <Route path="/mockups/30" element={<Suspense fallback={<SuspenseFallback />}><Mockup30 /></Suspense>} />
          <Route path="/mockups/31" element={<Suspense fallback={<SuspenseFallback />}><Mockup31 /></Suspense>} />
          <Route path="/mockups/32" element={<Suspense fallback={<SuspenseFallback />}><Mockup32 /></Suspense>} />
          <Route path="/mockups/33" element={<Suspense fallback={<SuspenseFallback />}><Mockup33 /></Suspense>} />
          <Route path="/mockups/34" element={<Suspense fallback={<SuspenseFallback />}><Mockup34 /></Suspense>} />
          <Route path="/mockups/35" element={<Suspense fallback={<SuspenseFallback />}><Mockup35 /></Suspense>} />
          <Route path="/mockups/36" element={<Suspense fallback={<SuspenseFallback />}><Mockup36 /></Suspense>} />
          <Route path="/mockups/37" element={<Suspense fallback={<SuspenseFallback />}><Mockup37 /></Suspense>} />
          <Route path="/mockups/38" element={<Suspense fallback={<SuspenseFallback />}><Mockup38 /></Suspense>} />
          <Route path="/mockups/39" element={<Suspense fallback={<SuspenseFallback />}><Mockup39 /></Suspense>} />
          <Route path="/mockups/40" element={<Suspense fallback={<SuspenseFallback />}><Mockup40 /></Suspense>} />
          <Route path="/_health/throw" element={<HealthThrowPage />} />
          {/*
           * Generic /login: email + password sign-in. After signin the venue
           * slug is resolved from the JWT's app_metadata.venue_id and the
           * merchant lands on /merchant/<slug>/audience.
           */}
          <Route
            path="/login"
            element={
              <Suspense fallback={<SuspenseFallback />}>
                <MerchantLogin />
              </Suspense>
            }
          />
          <Route
            path="/signup"
            element={
              <Suspense fallback={<SuspenseFallback />}>
                <MerchantSignup />
              </Suspense>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <Suspense fallback={<SuspenseFallback />}>
                <ForgotPassword />
              </Suspense>
            }
          />
          <Route
            path="/reset-password"
            element={
              <Suspense fallback={<SuspenseFallback />}>
                <ResetPassword />
              </Suspense>
            }
          />
          {/*
           * Plan 10-09: Merchant Self-Service Dashboard. Slug-prefixed nested
           * routes per CONTEXT D-01 (URL-vs-JWT slug mismatch becomes a
           * security signal). MerchantShell is the parent layout (sticky
           * header + Tabs nav + auth guard + dirty-form provider + Outlet);
           * 5 children. Bare /merchant/<slug>/ → /audience per D-02.
           */}
          <Route
            path="/merchant/:slug/*"
            element={
              <Suspense fallback={<SuspenseFallback />}>
                <MerchantShell />
              </Suspense>
            }
          >
            <Route
              path="login"
              element={
                <Suspense fallback={<SuspenseFallback />}>
                  <MerchantAuth />
                </Suspense>
              }
            />
            <Route
              path="overview"
              element={
                <Suspense fallback={<SuspenseFallback />}>
                  <MerchantOverview />
                </Suspense>
              }
            />
            <Route
              path="audience"
              element={
                <Suspense fallback={<SuspenseFallback />}>
                  <MerchantAudience />
                </Suspense>
              }
            />
            <Route
              path="landing"
              element={
                <Suspense fallback={<SuspenseFallback />}>
                  <MerchantLanding />
                </Suspense>
              }
            />
            <Route
              path="design"
              element={
                <Suspense fallback={<SuspenseFallback />}>
                  <MerchantDesignStudio />
                </Suspense>
              }
            />
            <Route
              path="perk"
              element={
                <Suspense fallback={<SuspenseFallback />}>
                  <MerchantPerk />
                </Suspense>
              }
            />
            <Route
              path="perks"
              element={
                <Suspense fallback={<SuspenseFallback />}>
                  <MerchantPerks />
                </Suspense>
              }
            />
            <Route
              path="menu"
              element={
                <Suspense fallback={<SuspenseFallback />}>
                  <MerchantMenu />
                </Suspense>
              }
            />
            <Route
              path="campaigns"
              element={
                <Suspense fallback={<SuspenseFallback />}>
                  <MerchantCampaigns />
                </Suspense>
              }
            />
            <Route
              path="receipts"
              element={
                <Suspense fallback={<SuspenseFallback />}>
                  <MerchantReceiptReviewQueue />
                </Suspense>
              }
            />
            <Route
              path="rewards"
              element={
                <Suspense fallback={<SuspenseFallback />}>
                  <MerchantRewards />
                </Suspense>
              }
            />
            <Route
              path="scan"
              element={
                <Suspense fallback={<SuspenseFallback />}>
                  <MerchantScan />
                </Suspense>
              }
            />
            <Route
              path="equipe"
              element={
                <Suspense fallback={<SuspenseFallback />}>
                  <MerchantEquipe />
                </Suspense>
              }
            />
            <Route index element={<Navigate to="overview" replace />} />
          </Route>
          {/*
           * Plan 09-06: Operator Admin Console. Network-wide (NOT slug-
           * prefixed) per CONTEXT D-01 — operators have no venue binding.
           * AdminShell is the parent layout (sticky header + 7-tab nav + auth
           * guard + dirty-form provider + Outlet). This plan mounts `login` +
           * `operators` + the index Navigate; Plans 09-07/08/09 add the other
           * section children. The index → /admin/venues target is mounted by
           * Plan 09-07 (transient interim target until then — documented).
           */}
          <Route
            path="/admin/*"
            element={
              <Suspense fallback={<SuspenseFallback />}>
                <AdminShell />
              </Suspense>
            }
          >
            <Route
              path="login"
              element={
                <Suspense fallback={<SuspenseFallback />}>
                  <AdminAuth />
                </Suspense>
              }
            />
            <Route
              path="operators"
              element={
                <Suspense fallback={<SuspenseFallback />}>
                  <AdminOperators />
                </Suspense>
              }
            />
            <Route
              path="venues"
              element={
                <Suspense fallback={<SuspenseFallback />}>
                  <AdminVenues />
                </Suspense>
              }
            />
            <Route
              path="qr"
              element={
                <Suspense fallback={<SuspenseFallback />}>
                  <AdminQrCodes />
                </Suspense>
              }
            />
            <Route
              path="tiers"
              element={
                <Suspense fallback={<SuspenseFallback />}>
                  <AdminTiers />
                </Suspense>
              }
            />
            <Route
              path="deletion-queue"
              element={
                <Suspense fallback={<SuspenseFallback />}>
                  <AdminDeletionQueue />
                </Suspense>
              }
            />
            <Route
              path="campaigns"
              element={
                <Suspense fallback={<SuspenseFallback />}>
                  <AdminCampaigns />
                </Suspense>
              }
            />
            <Route
              path="audiences"
              element={
                <Suspense fallback={<SuspenseFallback />}>
                  <AdminAudiences />
                </Suspense>
              }
            />
            <Route
              path="network-campaigns"
              element={
                <Suspense fallback={<SuspenseFallback />}>
                  <AdminNetworkCampaigns />
                </Suspense>
              }
            />
            <Route
              path="pixel-log"
              element={
                <Suspense fallback={<SuspenseFallback />}>
                  <AdminPixelLog />
                </Suspense>
              }
            />
            <Route
              path="phone-migration"
              element={
                <Suspense fallback={<SuspenseFallback />}>
                  <AdminPhoneMigration />
                </Suspense>
              }
            />
            <Route
              path="flagged"
              element={
                <Suspense fallback={<SuspenseFallback />}>
                  <AdminFlaggedNumbers />
                </Suspense>
              }
            />
            <Route index element={<Navigate to="venues" replace />} />
          </Route>
        </Routes>
      </BrowserRouter>
      {/* Global sonner outlet — dozens of components fire toast.success/error;
          without this mount every one of them rendered nothing (all merchant
          validation/save feedback was silently swallowed until 2026-07-31). */}
      <Toaster position="top-center" />
    </QueryClientProvider>
  );
}
